import {
  BackButton,
  DebouncedDateInput,
  DebouncedTextInput,
  Group,
  GroupItem,
  GroupTitle,
  Toolbar,
  useFieldLabel,
  useSaveMutation,
} from "ra-friendsofbabba";
import {
  ContractMonthsAlert,
  NewResearcherAlert,
  PerformedActivitiesAlert,
  StateAlert,
  StatsAlert,
  UnaccountableWpAlert,
} from "./alert";
import {
  DateInput,
  NumberInput,
  SaveButton,
  SimpleForm,
  email,
  required,
  useNotify,
  useTranslate,
} from "react-admin";
import { MediaInput, UserInput } from "../component/input";
import React, { useCallback, useEffect, useMemo } from "react";
import { useForm, useFormState } from "react-final-form";

import EpTargetStateInput from "./inputs/EpTargetStateInput";
import StartDateInput from "./inputs/StartDateInput";
import UserBreakReasonInput from "./inputs/UserBreakReasonInput";
import { get } from "lodash";
import { useHistory } from "react-router-dom";
import useIsDisabled from "./useIsDisabled";

const UserBreaksFormWrapper = ({ disabled, ...props }) => {
  const translate = useTranslate();
  const form = useForm();
  const fieldLabel = useFieldLabel({ resource: props?.resource });
  const { record } = props;
  const { values } = useFormState({ subscription: { values: true } });
  const { state, isResignation, isClosed, isInWorking, isNew, userId } =
    useMemo(() => {
      const isResignation =
        get(values, "user_break_reason_id", null) === 2 ? true : false;
      const state = get(values, "executive_project_target_state", null);
      const isClosed = state === "closed" ? true : false;
      const isInWorking = state === "in_working" ? true : false;
      const isNew = get(record, "created", null) === null ? true : false;
      const userId = get(values, "user_id", null);

      return { isResignation, isClosed, isInWorking, isNew, state, userId };
    }, [values, record]);

  useEffect(() => {
    if (!isResignation) {
      form.change("executive_project_target_state", null);
    }
    if (!isResignation || isClosed) {
      form.batch(() => {
        form.change("public_months", 0);
        form.change("private_months", 0);
        form.change("foreign_months", 0);
        form.change("contract_months", null);
        form.change("gen_user_name", null);
        form.change("gen_user_surname", null);
        form.change("gen_user_email", null);
        form.change("gen_user_fiscal_code", null);
      });
    }
  }, [isResignation, isClosed, form]);

  return (
    <Group wrapper fullWidth>
      <Group disabled={disabled}>
        <GroupItem lg={8} md={10} sm={12}>
          <UserInput
            source="user_id"
            filter={{ role: "researcher" }}
            fullWidth
            validate={required()}
            label={fieldLabel("user_id")}
          />
        </GroupItem>
      </Group>
      <Group disabled={disabled}>
        <GroupItem lg={8} md={10} sm={12}>
          <UserBreakReasonInput
            source="user_break_reason_id"
            fullWidth
            validate={required()}
            label={fieldLabel("user_break_reason_id")}
          />
        </GroupItem>
      </Group>
      <Group disabled={disabled}>
        <StartDateInput source="start" label={fieldLabel("start")} />
        <GroupItem lg={4} md={5} sm={6} xs={12}>
          <DateInput
            source="end"
            validate={required()}
            label={fieldLabel("end")}
          />
        </GroupItem>
      </Group>
      <DebouncedTextInput
        source="description"
        maxLength={4000}
        fullWidth
        multiline
        validate={required()}
        label={fieldLabel("description")}
      />
      <MediaInput
        source="media"
        validate={required()}
        label={fieldLabel("media")}
      />

      {isResignation && (
        <Group wrapper>
          <GroupTitle
            title={translate("resource.user-breaks.replacement.title")}
            subTitle={translate("resource.user-breaks.replacement.helper")}
          />
          <StateAlert />
          <Group disabled={disabled || !isNew}>
            <GroupItem lg={6} md={8} sm={12}>
              <EpTargetStateInput
                source="executive_project_target_state"
                validate={required()}
                label={fieldLabel("executive_project_target_state")}
              />
            </GroupItem>
          </Group>
        </Group>
      )}
      {isResignation && isInWorking && (
        <Group wrapper disabled={disabled || !isNew}>
          <PerformedActivitiesAlert />
          <StatsAlert isNew={isNew} userId={userId} />
          <Group>
            <GroupItem lg={4} md={4} sm={4}>
              <NumberInput
                source="public_months"
                helperText={translate(
                  "resources.user-breaks.fields.public_months.help"
                )}
                label={fieldLabel("public_months")}
              />
            </GroupItem>
            <GroupItem lg={4} md={4} sm={4}>
              <NumberInput
                source="private_months"
                helperText={translate(
                  "resources.user-breaks.fields.private_months.help"
                )}
                label={fieldLabel("private_months")}
              />
            </GroupItem>
            <GroupItem lg={4} md={4} sm={4}>
              <NumberInput
                source="foreign_months"
                helperText={translate(
                  "resources.user-breaks.fields.foreign_months.help"
                )}
                label={fieldLabel("foreign_months")}
              />
            </GroupItem>
          </Group>
          <ContractMonthsAlert />
          <UnaccountableWpAlert isNew={isNew} userId={userId} />
          <Group>
            <GroupItem lg={4} md={4} sm={4}>
              <NumberInput
                source="contract_months"
                helperText={translate(
                  "resources.user-breaks.fields.contract_months.help"
                )}
                label={fieldLabel("contract_months")}
                validate={required()}
              />
            </GroupItem>
          </Group>
        </Group>
      )}
      {isResignation && !isClosed && state !== null && (
        <Group wrapper disabled={disabled || !isNew}>
          <NewResearcherAlert />
          <Group>
            <GroupItem lg={6} md={6} sm={6}>
              <DebouncedTextInput
                source="gen_user_name"
                validate={required()}
                label={fieldLabel("gen_user_name")}
                maxLength={50}
              />
            </GroupItem>
            <GroupItem lg={6} md={6} sm={6}>
              <DebouncedTextInput
                source="gen_user_surname"
                validate={required()}
                label={fieldLabel("gen_user_surname")}
                maxLength={50}
              />
            </GroupItem>
          </Group>
          <Group>
            <GroupItem lg={6} md={6} sm={6}>
              <DebouncedTextInput
                source="gen_user_fiscal_code"
                validate={required()}
                label={fieldLabel("gen_user_fiscal_code")}
                maxLength={16}
              />
            </GroupItem>
            <GroupItem lg={6} md={6} sm={6}>
              <DebouncedTextInput
                source="gen_user_email"
                validate={[required(), email()]}
                label={fieldLabel("gen_user_email")}
                maxLength={255}
              />
            </GroupItem>
          </Group>
          <Group>
            <GroupItem lg={6} md={6} sm={6}>
              <DebouncedDateInput
                source="gen_user_hiring_date"
                validate={required()}
                label={fieldLabel("gen_user_hiring_date")}
              />
            </GroupItem>
          </Group>
        </Group>
      )}
    </Group>
  );
};

const Form = (props) => {
  const notify = useNotify();
  const history = useHistory();
  const disabled = useIsDisabled(props);
  const handleSuccess = useCallback(() => {
    notify("resource.user-breaks.messages.save_success");
    history.goBack();
  }, [notify, history]);
  const save = useSaveMutation({
    ...props,
    onSuccess: handleSuccess,
  });
  return (
    <SimpleForm
      {...props}
      save={save}
      initialValues={{
        public_months: 0,
        private_months: 0,
        foreign_months: 0,
      }}
      toolbar={
        <Toolbar useCustomButtons>
          <SaveButton disabled={disabled} />
          <BackButton mode="history" />
        </Toolbar>
      }
    >
      <UserBreaksFormWrapper {...props} disabled={disabled} fullWidth />
    </SimpleForm>
  );
};
export default Form;
